import { useEffect, useMemo, useRef } from 'react';
import type { Folder } from './useFolders';
import { useUploadProgress } from './useUploadProgress';

const UNSCOPED_SYNC_SOURCES = new Set(['outlook', 'imanage', 'onedrive', 'sharepoint']);

/**
 * Checks if `folderId` is the root folder itself or one of its descendants.
 */
function isWithinFolderTree(
  rootFolderId: number,
  folderId: number,
  parentFolderIdByFolderId: Map<number, number | undefined>,
): boolean {
  let candidateId: number | undefined = folderId;
  const visited = new Set<number>();
  while (candidateId !== undefined && !visited.has(candidateId)) {
    if (candidateId === rootFolderId) return true;
    visited.add(candidateId);
    candidateId = parentFolderIdByFolderId.get(candidateId);
  }
  return false;
}

function foldersShareLineage(
  folderId: number,
  otherFolderId: number,
  parentFolderIdByFolderId: Map<number, number | undefined>,
): boolean {
  return (
    isWithinFolderTree(folderId, otherFolderId, parentFolderIdByFolderId) ||
    isWithinFolderTree(otherFolderId, folderId, parentFolderIdByFolderId)
  );
}

/**
 * Auto-refreshes when an upload batch completes in the current folder, a descendant,
 * or an ancestor. Sync batches with no folder ID (Outlook/iManage/OneDrive v2) refresh
 * matter-wide because their files still land in destination folders.
 */
export function useBatchCompletionRefresh(
  matterGuid: string | undefined,
  currentFolderId: number | number[] | undefined,
  folders: Folder[],
  onRefresh: () => void
) {
  const { activeBatches } = useUploadProgress(matterGuid);
  const prevStatusesRef = useRef<Record<string, string>>({});
  const parentFolderIdByFolderId = useMemo(
    () => new Map(folders.map((folder) => [folder.folder_id, folder.parent_folder_id])),
    [folders],
  );

  useEffect(() => {
    const prevStatuses = prevStatusesRef.current;
    let shouldRefresh = false;

    for (const batch of activeBatches) {
      const prevStatus = prevStatuses[batch.batchTaskId];
      // Detect transition TO completed (skip if we never saw it before)
      if (
        batch.status === 'completed' &&
        prevStatus !== undefined &&
        prevStatus !== 'completed'
      ) {
        const batchFolderId = batch.folderId;
        const currentFolderIds = currentFolderId == null
          ? []
          : Array.isArray(currentFolderId)
            ? currentFolderId
            : [currentFolderId];
        if (batchFolderId == null) {
          if (UNSCOPED_SYNC_SOURCES.has(batch.source)) {
            shouldRefresh = true;
          }
        } else if (
          currentFolderIds.some((folderId) => (
            foldersShareLineage(folderId, batchFolderId, parentFolderIdByFolderId)
          ))
        ) {
          shouldRefresh = true;
        }
      }
    }

    // Update tracked statuses
    const newStatuses: Record<string, string> = {};
    for (const batch of activeBatches) {
      newStatuses[batch.batchTaskId] = batch.status;
    }
    prevStatusesRef.current = newStatuses;

    if (shouldRefresh) {
      onRefresh();
    }
  }, [activeBatches, currentFolderId, parentFolderIdByFolderId, onRefresh]);
}
